import type { ArtistSignalItem, BlackboardNote, TimelineEvent } from "@/api/observability";
import type { ObservabilityThread } from "@/api/observability";
import { fetchArtistSignals } from "@/api/observability";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ThreadDrawerState = {
  thread: ObservabilityThread;
  notes: BlackboardNote[];
};

/** Re-export the canonical type from the API layer so consumers only need one import. */
export type ArtistSignal = ArtistSignalItem & { highlight?: string };

export type ConversationItem = {
  agent: string;
  summary: string;
  createdAt?: string;
  status?: string;
};

export type UpcomingEvent = {
  id: string;
  title: string;
  date: string;
  source: string;
  location?: string;
};

export type ActionQueueItem = {
  id: string;
  label: string;
  description: string;
  ageMinutes?: number;
  severity: "high" | "medium" | "low";
  actionType: "hitl" | "stall";
  agent?: string;
};

export type TimeRangeKey = "15m" | "1h" | "6h" | "24h";

export type EventFilter = "all" | "actions" | "thoughts" | "hitl" | "errors" | "executed" | "delegations";

// ─── Constants ────────────────────────────────────────────────────────────────

export const TIME_RANGES: Record<TimeRangeKey, { label: string; minutes: number }> = {
  "15m": { label: "Last 15m", minutes: 15 },
  "1h": { label: "Last 1h", minutes: 60 },
  "6h": { label: "Last 6h", minutes: 360 },
  "24h": { label: "Last 24h", minutes: 1440 },
};

// ─── Utility Functions ────────────────────────────────────────────────────────

export function formatDate(value?: string) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function formatRelativeTime(value?: string) {
  if (!value) return "";
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  const diff = Date.now() - ts;
  const minutes = Math.round(diff / 60000);
  if (minutes <= 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function summarizeNote(note: BlackboardNote) {
  if (note.thought) return note.thought;
  if (note.action) return note.action;
  if (note.observation) return JSON.stringify(note.observation);
  if (note.payload) return JSON.stringify(note.payload);
  return "";
}

export function getEventBucket(event: TimelineEvent): EventFilter {
  const type = (event.event_type || "").toLowerCase();
  if (type.includes("error") || type.includes("failed")) return "errors";
  if (type.includes("pending_hitl") || type.includes("hitl")) return "hitl";
  if (type.startsWith("action")) return "actions";
  if (type.includes("thought")) return "thoughts";
  if (type.includes("observation") || type.includes("executed")) return "executed";
  if (type.includes("delegation")) return "delegations";
  return "all";
}

export function findArtistName(source?: unknown): string | null {
  if (!source) return null;
  if (typeof source === "string") {
    if (source.length < 3) return null;
    const match = source.match(/artist\s*[:\-]\s*([A-Za-z0-9 &'.-]+)/i);
    return match?.[1]?.trim() ?? null;
  }
  if (typeof source !== "object") return null;
  const record = source as Record<string, unknown>;
  const direct =
    record.artist_name ||
    record.artistName ||
    record.artist ||
    record.artist_id ||
    record.artistId ||
    record.primary_artist ||
    record.primaryArtist;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  for (const key of Object.keys(record)) {
    if (!key.toLowerCase().includes("artist")) continue;
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/** Fetch structured artist signals from the backend (replaces heuristic string parsing). */
export async function fetchArtistSignalsData(): Promise<ArtistSignal[]> {
  return fetchArtistSignals();
}

export function buildConversationItems(notes: BlackboardNote[]): ConversationItem[] {
  return notes.slice(0, 6).map((note) => ({
    agent: note.agent ?? "agent",
    summary: summarizeNote(note) || "No details",
    createdAt: note.created_at,
    status: note.status ?? "update",
  }));
}

export function getMomentumTone(momentum: ArtistSignal["momentum"]) {
  if (momentum === "up") return "text-emerald-200 bg-emerald-500/10 border-emerald-400/40";
  if (momentum === "down") return "text-rose-200 bg-rose-500/10 border-rose-400/40";
  return "text-sky-200 bg-sky-500/10 border-sky-400/40";
}

export function formatNumber(value: number) {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(value);
}

export function formatShortDate(value?: string) {
  if (!value) return "TBD";
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function normalizeCalendarItem(source: string, item: Record<string, unknown>): UpcomingEvent | null {
  const startRaw = item.start ?? item.start_time ?? item.date ?? item.scheduled_for ?? item.show_date;
  if (!startRaw) return null;
  const title = item.title ?? item.name ?? item.venue_name ?? "Upcoming event";
  const location = item.city ?? item.market ?? item.location ?? item.venue_city;
  return {
    id: String(item.id ?? `${source}-${startRaw}`),
    title: String(title),
    date: String(startRaw),
    source,
    location: location ? String(location) : undefined,
  };
}

export function minutesSince(timestamp?: string) {
  if (!timestamp) return null;
  const ts = Date.parse(timestamp);
  if (Number.isNaN(ts)) return null;
  return Math.round((Date.now() - ts) / 60000);
}

export function severityTone(severity: ActionQueueItem["severity"]) {
  if (severity === "high") return "border-rose-400/60 bg-rose-500/10 text-rose-200";
  if (severity === "medium") return "border-amber-400/60 bg-amber-500/10 text-amber-200";
  return "border-sky-400/60 bg-sky-500/10 text-sky-200";
}
